import { supabase } from './client';
import type { Project } from '../../types/project';
import type { Contact } from '../../types/contacts';

export async function createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at' | 'progress' | 'tasks'>) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .insert([project])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to create project:', error);
    throw error;
  }
}

export async function getProjects(entityId: string) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        tasks:project_tasks(id, title, status)
      `)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Calculate progress for each project
    return data.map(project => {
      const tasks = project.tasks || [];
      const totalTasks = tasks.length;
      const completedTasks = tasks.filter((task: any) => task.status === 'completed').length;
      const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
      
      return {
        ...project,
        progress,
        tasks: {
          total: totalTasks,
          completed: completedTasks
        }
      };
    });
  } catch (error) {
    console.error('Failed to get projects:', error);
    throw error;
  }
}

export async function getProject(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        tasks:project_tasks(*)
      `)
      .eq('id', projectId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to get project:', error);
    throw error;
  }
}

export async function updateProject(projectId: string, updates: Partial<Omit<Project, 'id' | 'created_at' | 'updated_at' | 'progress' | 'tasks'>>) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to update project:', error);
    throw error;
  }
}

export async function deleteProject(projectId: string) {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to delete project:', error);
    throw error;
  }
}

export async function getTeamContacts(entityId: string) {
  try {
    const { data, error } = await supabase
      .from('contacts')
      .select(`
        id,
        first_name,
        last_name,
        title,
        organization_id,
        emails:contact_emails(id, email, type, is_primary)
      `)
      .eq('entity_id', entityId)
      .eq('type', 'team');

    if (error) throw error;
    return data as Contact[];
  } catch (error) {
    console.error('Failed to get team contacts:', error);
    return [];
  }
}

export async function getProjectTeamMembers(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('project_team_members')
      .select(`
        id,
        role,
        contact:contacts(
          id,
          first_name,
          last_name,
          title,
          emails:contact_emails(id, email, type, is_primary)
        )
      `)
      .eq('project_id', projectId);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to get project team members:', error);
    return [];
  }
}

export async function addTeamMemberToProject(projectId: string, contactId: string, role?: string) {
  try {
    const { data, error } = await supabase
      .from('project_team_members')
      .insert([{
        project_id: projectId,
        contact_id: contactId,
        role
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to add team member to project:', error);
    throw error;
  }
}

export async function removeTeamMemberFromProject(teamMemberId: string) {
  try {
    const { error } = await supabase
      .from('project_team_members')
      .delete()
      .eq('id', teamMemberId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to remove team member from project:', error);
    throw error;
  }
}

// Add a team member to a project
export async function addProjectTeamMember(teamMember: {
  project_id: string;
  contact_id: string;
  role?: string;
}) {
  try {
    const { data, error } = await supabase
      .from('project_team_members')
      .insert([teamMember])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Failed to add project team member:', error);
    throw error;
  }
}